import React, { createContext, useEffect, useRef } from "react";

import { SelectedParticipantProvider } from "./useSelectedParticipant/useSelectedParticipant";
import useHandleRoomDisconnectionErrors from "./useHandleRoomDisconnectionErrors/useHandleRoomDisconnectionErrors";
import useHandleOnDisconnect from "./useHandleOnDisconnect/useHandleOnDisconnect";
import useHandleTrackPublicationFailed from "./useHandleTrackPublicationFailed/useHandleTrackPublicationFailed";
import useLocalTracks from "./useLocalTracks/useLocalTracks";
import useRoom from "./useRoom/useRoom";
import useScreenShareToggle from "./useScreenShareToggle/useScreenShareToggle";
import useActiveAudioSinkId from "./useActiveAudioSinkId/useActiveAudioSinkId";
import useAudioDeviceTelemetry from "hooks/livekit/useAudioDeviceTelemetry/useAudioDeviceTelemetry";
import { useAppStateContext } from "../../useAppState/useAppState";
import { useSessionContext } from "../../useSession/useSession";
import { useAuthUserDataContext } from "../../useAuthUserData/useAuthUserData";
import { logLiveKit, LogLevel } from "utils/sentryLogger";
import { RoomEvent } from "livekit-client";

export const LiveKitProgrammableVideoContext = createContext(null);

// Module-level flag to prevent repeated error logs across component remounts
let hasLoggedMediaError = false;

/*
 *  The hooks used by the Provider component are different from the hooks found in the 'hooks/livekit' directory. The hooks
 *  in the 'livekit/hooks/' directory can be used anywhere in a video application, and they can be used any number of times.
 *  the hooks in the 'useLiveKitProgrammableVideo/' directory are intended to be used by the Provider component only. Using these hooks
 *  elsewhere in the application may cause problems as these hooks should not be used more than once in an application.
 */

export function LiveKitProgrammableVideoProvider({
  connectionOptions,
  roomOptions,
  children,
  onError = () => {},
  onDisconnect = () => {},
}) {
  const onErrorCallback = (error) => {
    console.log(`ERROR: ${error.message}`, error);
    onError(error);
  };

  const { isSpectator, groupCode: appGroupCode } = useAppStateContext();
  const sessionContext = useSessionContext();
  const { authUserData } = useAuthUserDataContext();
  // Use session context values with app state as fallback (app state is set earlier in the flow)
  const sessionId = sessionContext?.session?.id;
  const groupCode = sessionContext?.groupCode || appGroupCode;
  const userId = authUserData?.id;

  const {
    localTracks,
    isAcquiringLocalTracks,
    getAudioAndVideoTracks,
    retryTrackAcquisition,
    isVideoMuted,
  } = useLocalTracks();

  const { room, isConnecting, connect, flushConnectionEventsSummary } = useRoom(
    isSpectator ? [] : localTracks,
    onErrorCallback,
    connectionOptions,
    roomOptions,
    { sessionId, userId, groupCode }
  );

  const [activeAudioSinkId, setActiveAudioSinkId] = useActiveAudioSinkId();
  const audioDevices = useAudioDeviceTelemetry({ localTracks, activeAudioSinkId, sessionId, userId, groupCode });
  // A saved sink that the browser no longer lists, or lists without a label
  // (permission not granted yet), is one Chrome will refuse. LiveKit fires
  // setSinkId without awaiting it, so that refusal escapes every catch as an
  // unhandled rejection; skip it here and log what was skipped instead.
  const canApplySink = activeAudioSinkId === "default" || !!audioDevices.speaker_label;
  const warnedSinkRef = useRef(null);

  // With webAudioMix the remote audio comes out of LiveKit's shared
  // AudioContext, so the chosen output device has to be applied there — the
  // per-element setSinkId in AudioTrack no longer routes the sound. Re-applied
  // on connect because the context only exists from then on. Skipped where
  // output selection doesn't exist (iOS): LiveKit would otherwise try to
  // enumerate output devices and, finding none, re-request the microphone —
  // a second permission prompt mid-session.
  useEffect(() => {
    if (typeof room?.switchActiveDevice !== "function") return undefined;
    if (typeof HTMLMediaElement === "undefined" || !("setSinkId" in HTMLMediaElement.prototype)) return undefined;
    const apply = () => {
      if (!canApplySink) {
        if (warnedSinkRef.current !== activeAudioSinkId) {
          warnedSinkRef.current = activeAudioSinkId;
          logLiveKit("Audio sink switch skipped", {
            level: LogLevel.WARNING,
            event: "audio_sink_switch_skipped",
            sessionId,
            userId,
            groupCode,
            sinkId: activeAudioSinkId,
          });
        }
        return;
      }
      room.switchActiveDevice("audiooutput", activeAudioSinkId).catch(() => {});
    };
    apply();
    room.on(RoomEvent.Connected, apply);
    return () => {
      room.off(RoomEvent.Connected, apply);
    };
  }, [room, activeAudioSinkId, canApplySink, sessionId, userId, groupCode]);

  // Register onError and onDisconnect callback functions.
  useHandleRoomDisconnectionErrors(room, onError);
  useHandleTrackPublicationFailed(room, onError);
  useHandleOnDisconnect(room, onDisconnect);

  const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(
    room,
    onError
  );

  // Acquire local tracks on mount
  useEffect(() => {
    getAudioAndVideoTracks().catch((error) => {
      // Only log once to avoid flooding console (module-level flag persists across remounts)
      // Ignore video source errors - only audio/mic errors are relevant at this step
      const isVideoError = error?.message?.toLowerCase().includes("video");
      if (!hasLoggedMediaError && !isVideoError) {
        hasLoggedMediaError = true;
        // Permission denial and missing devices are user/environment issues, not system errors
        const isUserEnvironmentError = error?.name === "NotAllowedError" || error?.name === "NotFoundError";
        logLiveKit("Error acquiring local media", {
          level: isUserEnvironmentError ? LogLevel.WARNING : LogLevel.ERROR,
          event: "local_media_acquisition_failed",
          sessionId,
          userId,
          groupCode,
          errorName: error?.name,
          errorMessage: error?.message,
        });
      }
    });
  }, [getAudioAndVideoTracks, sessionId, userId, groupCode]);

  return (
    <LiveKitProgrammableVideoContext.Provider
      value={{
        room,
        localTracks,
        isConnecting,
        onError: onErrorCallback,
        onDisconnect,
        connect,
        isAcquiringLocalTracks,
        isSharingScreen,
        toggleScreenShare,
        getAudioAndVideoTracks,
        retryTrackAcquisition,
        activeAudioSinkId,
        setActiveAudioSinkId,
        isVideoMuted,
        flushConnectionEventsSummary,
      }}
    >
      <SelectedParticipantProvider room={room}>
        {children}
      </SelectedParticipantProvider>
    </LiveKitProgrammableVideoContext.Provider>
  );
}
